import React, { useState } from 'react'
import styles from './JobInformation.module.css'
import { JobList } from './components/JobList/JobList'
import { FilterButton } from './components/FilterButton/FilterButton'
import { Job, JobFilters, Config, FilterType, TabType, JobInformationProps, JobUtilsInterface } from './types'
import { useAbTestGtm } from '../../../../hooks/useAbTestGtm'

const IMAGE = {
    lowarrow: {
        src: '/pages/google/JobInformation/lowarrow.svg',
        alt: '飲食求人イメージ'
    },
    pinIcon: {
        src: '/pages/google/JobInformation/pinIcon.svg',
        alt: 'エリアアイコン'
    },
    flypan: {
        src: '/pages/google/JobInformation/flypan.svg',
        alt: '料理ジャンルアイコン'
    },
    cookhat: {
        src: '/pages/google/JobInformation/cookhat.svg',
        alt: '職種アイコン'
    }
}

const JobUtils: JobUtilsInterface = {
    filterJobs: (jobs: Job[], filters: JobFilters, config: Config): Job[] => {
        return jobs.filter(job => {
            if (filters.area) {
                const areaJobIds = config.areaMapping[filters.area]
                if (areaJobIds && !areaJobIds.includes(job.id)) {
                    return false
                }
            }

            if (filters.genre) {
                const genreJobIds = config.genreMapping[filters.genre]
                if (genreJobIds && !genreJobIds.includes(job.id)) {
                    return false
                }
            }

            if (filters.position) {
                const positionJobIds = config.positionMapping[filters.position]
                if (positionJobIds && !positionJobIds.includes(job.id)) {
                    return false
                }
            }

            return true
        })
    },

    addDataClickLabels: (jobs: Job[], selectedFilters: JobFilters): Job[] => {
        const ordinalNumbers = ['1st', '2nd', '3rd', '4th', '5th']

        return jobs.map((job, index) => {
            let dataClickLabel = ''

            if (selectedFilters.area) {
                dataClickLabel = `CT_Jobinformation_エリア-${selectedFilters.area}-${ordinalNumbers[index] || `${index + 1}th`}`
            } else if (selectedFilters.genre) {
                dataClickLabel = `CT_Jobinformation_ジャンル-${selectedFilters.genre}-${ordinalNumbers[index] || `${index + 1}th`}`.replace('・','')
            } else if (selectedFilters.position) {
                dataClickLabel = `CT_Jobinformation_職種-${selectedFilters.position}-${ordinalNumbers[index] || `${index + 1}th`}`
            }

            return {
                ...job,
                dataClickLabel
            }
        })
    }
}

const useJobSearch = (config: Config): {
    selectedFilters: JobFilters
    handleFilterChange: (_filterType: FilterType, _value: string) => void
    resetFiltersForTab: (_activeTab: TabType) => void
    filteredJobs: Job[]
} => {
    const [selectedFilters, setSelectedFilters] = useState<JobFilters>({
        area: config.filtersList.area[0]?.areaName ?? '',
        genre: '',
        position: ''
    })

    const handleFilterChange = (filterType: FilterType, value: string) => {
        setSelectedFilters(prev => ({
            ...prev,
            [filterType]: value
        }))
    }

    const resetFiltersForTab = (activeTab: TabType) => {
        switch(activeTab) {
            case 'area':
                setSelectedFilters({
                    area: config.filtersList.area[0]?.areaName ?? '',
                    genre: '',
                    position: ''
                })
                break
            case 'genre':
                setSelectedFilters({
                    area: '',
                    genre: config.filtersList.genre[0]?.genreName ?? '',
                    position: ''
                })
                break
            case 'position':
                setSelectedFilters({
                    area: '',
                    genre: '',
                    position: config.filtersList.position[0]?.positionName ?? ''
                })
                break
            default:
                break
        }
    }

    const filteredJobs = React.useMemo(() => {
        const filtered = JobUtils.filterJobs(config.jobs, selectedFilters, config)
        return JobUtils.addDataClickLabels(filtered, selectedFilters)
    }, [selectedFilters, config])

    return {
        selectedFilters,
        handleFilterChange,
        resetFiltersForTab,
        filteredJobs
    }
}

const JobInformation = ({ onJobClick, config, dataViewLabel, startRef, area = 'nagoya' }: JobInformationProps) => {
    const { dataViewLabel: gtmDataViewLabel, dataClickLabel } = useAbTestGtm()
    const { selectedFilters, handleFilterChange, resetFiltersForTab, filteredJobs } = useJobSearch(config)

    const [activeTab, setActiveTab] = useState<TabType>('area')

    const getTabStyle = (tabType: TabType) => {
        return activeTab === tabType
            ? styles.activeTab
            : styles.inactiveTab
    }

    const handleTabChange = (tabType: TabType) => {
        setActiveTab(tabType)
        resetFiltersForTab(tabType)
    }

    const renderActiveFilters = (): React.JSX.Element[] | null => {
        switch(activeTab) {
            case 'area':
                return config.filtersList.area.map((areaItem) => (
                    <FilterButton
                        key={areaItem.areaName}
                        item={areaItem.areaName}
                        selectedValue={selectedFilters.area ?? ''}
                        onFilterChange={handleFilterChange}
                        filterType="area"
                        dataClickLabel={dataClickLabel(areaItem.dataClickLabel)}
                    />
                ))
            case 'genre':
                return config.filtersList.genre.map((genre) => (
                    <FilterButton
                        key={genre.genreName}
                        item={genre.genreName}
                        selectedValue={selectedFilters.genre ?? ''}
                        onFilterChange={handleFilterChange}
                        filterType="genre"
                        dataClickLabel={dataClickLabel(genre.dataClickLabel)}
                    />
                ))
            case 'position':
                return config.filtersList.position.map((position) => (
                    <FilterButton
                        key={position.positionName}
                        item={position.positionName}
                        selectedValue={selectedFilters.position ?? ''}
                        onFilterChange={handleFilterChange}
                        filterType="position"
                        dataClickLabel={dataClickLabel(position.dataClickLabel)}
                    />
                ))
            default:
                return null
        }
    }

    return (
        <section className={styles.JobInformationWrap} data-view-label={dataViewLabel || gtmDataViewLabel('CT_Jobinformation')}>
            <div className={styles.orangeBg}></div>
            <h4>
                <span className={styles.emphasisDots}>希望条件</span>に近い<br/>
                <span className={styles.marker}>飲食求人を見てみましょう！</span>
            </h4>
            <img className={styles.jobInformationImage} src={IMAGE.lowarrow.src}
                 alt={IMAGE.lowarrow.alt}/>
            <div className={styles.jobSearchWrap}>
                <p className={styles.jobSearchGuidanceText}>
                    <img
                        src="/pages/google/JobInformation/tapIcon.svg"
                        alt="タップアイコン"
                        style={{ width: '21px', height: '25px' }}
                    />
                    タップで希望の求人を探してみましょう
                </p>

                <div className={styles.tabContainer}>
                    <button
                        className={`${styles.tabButton} ${getTabStyle('area')}`}
                        onClick={() => handleTabChange('area')}
                        data-click-label={dataClickLabel(config.jobInformationData.dataClickLabels.area)}
                    >
                        <img src={IMAGE.pinIcon.src} alt={IMAGE.pinIcon.alt}/>
                        エリア
                    </button>
                    <button
                        className={`${styles.tabButton} ${getTabStyle('genre')}`}
                        onClick={() => handleTabChange('genre')}
                        data-click-label={dataClickLabel(config.jobInformationData.dataClickLabels.genre)}
                    >
                        <img src={IMAGE.flypan.src} alt={IMAGE.flypan.alt}/>
                        料理ジャンル
                    </button>
                    <button
                        className={`${styles.tabButton} ${getTabStyle('position')}`}
                        onClick={() => handleTabChange('position')}
                        data-click-label={dataClickLabel(config.jobInformationData.dataClickLabels.position)}
                    >
                        <img src={IMAGE.cookhat.src} alt={IMAGE.cookhat.alt}/>
                        職種
                    </button>
                </div>

                <div className={styles.filterButtonContainer} ref={startRef}>
                    {renderActiveFilters()}
                </div>

                <JobList
                    jobs={filteredJobs}
                    activeTab={activeTab}
                    onJobClick={onJobClick}
                    config={config}
                    area={area}
                />
            </div>
        </section>
    )
}

export default JobInformation